import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthStore } from '../auth/auth.store';
import { decodeJwtPayload } from '../auth/jwt';

/**
 * Functional canActivate guard.
 *
 * 1. No token → redirect /login
 * 2. No `allowedSubjectTypes` in route data → allow (backward compatible)
 * 3. JWT `subjectType` matches allowedTypes → allow
 * 4. JWT `subjectType` does not match → redirect /login
 * 5. Invalid token → redirect /login
 */
export const authGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const token = authStore.accessToken();

  if (!token || !authStore.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  // Check allowedSubjectTypes if present in route data (D-03)
  const allowedTypes = route.data?.['allowedSubjectTypes'] as
    | ('user' | 'member')[]
    | undefined;

  if (allowedTypes) {
    try {
      const payload = decodeJwtPayload(token);
      const subjectType = payload.subjectType as 'user' | 'member' | undefined;
      if (!subjectType || !allowedTypes.includes(subjectType)) {
        return router.createUrlTree(['/login']);
      }
    } catch {
      // Invalid token — redirect to login
      return router.createUrlTree(['/login']);
    }
  }

  return true;
};
